import { zodResolver } from '@hookform/resolvers/zod'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCountries } from '../../../api/countries/get-countries'
import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  InsuranceTypeEnum,
  insuranceTypeEnumMap
} from '../../../lib/types/enums/insurance/insurance-type.enum'
import { getCountryLabel } from '../../../lib/types/models/country/country-list'
import { InsuranceInput } from '../../../lib/types/models/insurance/insurance-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import LoadingScreen from '../../elements/loading/loading-screen'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<NullableTransformer<InsuranceInput>> = z.object({
  name: createStringSchema('NOM'),
  description: createStringSchema('description', { nullish: true }),
  type: createEnumSchema({ fieldName: 'type', enumType: InsuranceTypeEnum }),
  price: createNumberSchema('prix'),
  elligibleResidenceCountries: z.array(createStringSchema('pays de résidence'), {
    required_error: 'Veuillez sélectionner au moins un pays de résidence',
    message: 'Veuillez sélectionner au moins un pays de résidence'
  }),
  elligibleDestinationCountries: z.array(createNumberSchema('pays de destination'), {
    required_error: 'Veuillez sélectionner au moins un pays de destination',
    message: 'Veuillez sélectionner au moins un pays de destination'
  })
})

export const InsuranceForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<InsuranceInput>) => {
  const { currentLanguage } = useTranslate()

  const form = useForm<InsuranceInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  if (!countries || !countryList) return <LoadingScreen />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'py-3 rounded-2xl flex flex-col gap-4'}>
          <div className={'flex gap-2 justify-between'}>
            <FormField
              control={form.control}
              name={'name'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} required type={'text'} label={'NOM'} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'description'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} type={'text'} label={'Description'} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className={'flex gap-2 justify-between'}>
            <FormField
              control={form.control}
              name={'type'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      label={'Type de assurance'}
                      options={Object.values(InsuranceTypeEnum).map((option) => ({
                        label: insuranceTypeEnumMap[option],
                        value: option
                      }))}
                      selectedOption={getSelectedOption(
                        field.value,
                        insuranceTypeEnumMap[field.value]
                      )}
                      onChange={(option) => field.onChange(option)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'price'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} required type={'number'} label={'Prix'} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className={'flex gap-2 justify-between'}>
            <FormField
              control={form.control}
              name={'elligibleResidenceCountries'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      required
                      searchable
                      variant={'primary'}
                      label={'Pays de résidence'}
                      selectedOptions={conditionalRender(
                        !!field.value,
                        field.value?.map((countryCode) => ({
                          label:
                            getCountryLabel(
                              currentLanguage,
                              countryList?.data.find((c) => c.key.toString() === countryCode)
                            ) ?? '',
                          value: countryCode
                        }))
                      )}
                      options={
                        countryList?.data.map((c) => ({
                          label: getCountryLabel(currentLanguage, c) ?? '',
                          value: c.key
                        })) ?? []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'elligibleDestinationCountries'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      required
                      label={'Pays de destination'}
                      variant={'primary'}
                      selectedOptions={field.value?.map((countryId) => ({
                        label: countries?.data.find((c) => c.id === countryId)?.name || '',
                        value: countryId
                      }))}
                      options={
                        countries?.data.map((country) => ({
                          label: country.name,
                          value: country.id
                        })) || []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}
